import {
  BarElement,
  CategoryScale,
  Chart,
  Chart as ChartJS,
  type ChartType,
  Legend,
  LinearScale,
  Title,
  Tooltip,
  type TooltipModel,
  type TooltipPositionerFunction,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { LoadingComponent } from '../../../../../components/loading/loading.component';
import { ReportsComponentProps } from '../../../../../components/reports/interfaces/reports-component-props';
import { useDataProvider } from '../../../../../hooks/data-provider/data-provider.hook';
import { useResize } from '../../../../../hooks/use-window-resize.hook';
import { SortBy } from '../../../../../interfaces/sort-by.interface';
import { formatNumber } from '../../../../../utils/formatting/number-formatter.helper';
import { useWaterfallChartData } from '../hooks/use-waterfall-chart-data.hook';
import { WaterfallData } from '../hooks/waterfall-data.interface';
import { WaterfallTooltipComponent } from '../tooltip/waterfall-tooltip.component';
import './decomp-model-waterfall.component.scss';
import 'chart.js/auto';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const VARIABLES_FOR_EXCLUDING = ['PREDICTION', 'ACTUAL', 'BASE'];

interface DecompModelWaterfallComponentProps extends ReportsComponentProps {
  // height: number,
  // totalReportId: string,
  defaultSortBy?: SortBy[];
}

declare module 'chart.js' {
  interface TooltipPositionerMap {
    top: TooltipPositionerFunction<ChartType>;
  }
}

export const DecompModelWaterfallComponent = ({
  filterSelection,
  reportName: reportId,
  enabled,
  defaultSortBy,
  className,
}: DecompModelWaterfallComponentProps) => {
  const filterFunction = useCallback((a: Record<string, any>) => {
    return VARIABLES_FOR_EXCLUDING.indexOf(a.WATERFALL_NAME) === -1;
  }, []);

  const { data: completeData, isFetching } = useDataProvider<
    Record<string, any>
  >(reportId, filterSelection, defaultSortBy, enabled);
  const data = useMemo(() => {
    if (completeData) {
      return {
        ...completeData,
        rows: completeData.rows.filter(filterFunction),
      };
    }
  }, [completeData, filterFunction]);
  const chartData = useWaterfallChartData(data);

  const [tooltipData, setTooltipData] = useState<WaterfallData | undefined>();
  const [tooltipDataIndex, setTooltipDataIndex] = useState<
    number | undefined
  >();

  const chartRef = useRef<HTMLDivElement | null>(null);

  // Registering the custom positioner for the tooltip with explicit this context
  Tooltip.positioners.top = function (
    this: TooltipModel<ChartType>,
    elements,
    eventPosition,
  ) {
    const pos = Tooltip.positioners.average.call(this, elements, eventPosition); // Get the average position

    if (!pos) return false;

    const chart = this.chart; // Correctly reference the chart instance

    return {
      x: pos.x,
      y: chart.chartArea.top, // Position the tooltip at the top of the chart area
      xAlign: 'center',
      yAlign: 'bottom',
    };
  };

  const customTooltip = useCallback(
    ({
      chart,
      tooltip,
    }: {
      chart: Chart;
      tooltip: TooltipModel<ChartType>;
    }) => {
      const tooltipEl = chartRef.current;

      if (tooltipEl) {
        if (tooltip.opacity === 0) {
          // tooltipEl.style.opacity = '0';
          return;
        }
        if (tooltip.dataPoints) {
          setTooltipData(
            tooltip.dataPoints.map((i) => i.raw as WaterfallData)[0],
          );
          setTooltipDataIndex(tooltip.dataPoints[0].dataIndex);
          tooltipEl.style.opacity = '1';
          tooltipEl.style.pointerEvents = 'all';
        }

        const chartBoundaries = chart.chartArea;
        const { right, left } = chartBoundaries;

        const tooltipElBoundaries = tooltipEl.getBoundingClientRect();

        const offsetX = tooltipElBoundaries.width / 2;
        const offsetY = tooltipElBoundaries.height;
        const chartRight = right + left;
        const chartLeft = -left;
        const tooltipRight = tooltip.caretX + offsetX;
        const tooltipLeft = tooltip.caretX - offsetX;

        tooltipEl.style.position = 'absolute';
        tooltipEl.style.opacity = '1';
        tooltipEl.style.pointerEvents = 'all';
        if (tooltipRight > chartRight) {
          tooltipEl.style.right = '0px';
          tooltipEl.style.left = '';
        } else {
          if (tooltipLeft < chartLeft) {
            tooltipEl.style.left = '0px';
          } else {
            tooltipEl.style.left = `${tooltip.caretX - offsetX}px`;
          }
          tooltipEl.style.right = '';
        }
        tooltipEl.style.top = `${tooltip.caretY - offsetY}px`;
      }
    },
    [],
  );

  // Handle click outside to close the tooltip
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        chartRef.current &&
        !chartRef.current.contains(event.target as Node)
      ) {
        const tooltipEl = chartRef.current;
        if (tooltipEl) {
          tooltipEl.style.opacity = '0'; // Hide the tooltip
          tooltipEl.style.pointerEvents = 'none'; // Prevents from capture click events.
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (chartRef) {
      const tooltipEl = chartRef.current;
      if (tooltipEl) {
        tooltipEl.style.opacity = '0';
        tooltipEl.style.pointerEvents = 'none';
      }
    }
  }, [chartRef, data]);

  const options: any = useMemo(() => {
    return {
      aspectRatio: 16 / 4,
      plugins: {
        datalabels: {
          // Position of the labels
          // (start, end, center, etc.)
          // anchor: 'end',
          // // Alignment of the labels
          // // (start, end, center, etc.)
          align: 'end',
          // // Color of the labels
          color: '#101828',
          formatter: function (item: any) {
            // Display the actual data value
            return formatNumber(item.value);
          },
        },

        title: {
          display: true,
        },
        legend: {
          display: false,
        },
        tooltip: {
          position: 'nearest',
          enabled: false,
          external: customTooltip,
        },
      },
      responsive: true,
      scales: {
        x: {
          stacked: true,
        },
        y: {
          stacked: true,
        },
      },
      parsing: {
        xAxisKey: 'label',
        yAxisKey: 'chartValue',
      },
    };
  }, [customTooltip]);

  const bar = useRef<ChartJS<'bar', WaterfallData[], unknown> | null>(null);
  const onTooltipMouseOver = useCallback(
    (index: number | undefined) => {
      if (bar && bar.current && index !== undefined) {
        const chart = bar.current;
        chart.setActiveElements([
          {
            datasetIndex: 0,
            index,
          },
        ]);
        chart.update();
      }
    },
    [bar],
  );

  useResize(bar.current);

  return (
    <div className={`chart-component ${className ?? ''}`}>
      <LoadingComponent isLoading={isFetching}>
        <div className="d-flex ps-1">
          <div className="d-flex align-items-center">
            <div className="">
              <div className="color-circle increase" />
            </div>
            <div className="px-3">Positive driver</div>
          </div>
          <div className="d-flex align-items-center">
            <div className=" ">
              <div className="color-circle decrease" />
            </div>
            <div className="px-3">Negative driver</div>
          </div>
          <div className="d-flex align-items-center">
            <div className="">
              <div className="color-circle total " />
            </div>
            <div className="px-3">Total</div>
          </div>
        </div>
        <WaterfallTooltipComponent
          ref={chartRef}
          data={tooltipData}
          dataIndex={tooltipDataIndex}
          onMouseOver={onTooltipMouseOver}
        />
        <Bar
          options={options}
          data={chartData}
          plugins={[ChartDataLabels]}
          ref={bar}
        />
      </LoadingComponent>
    </div>
  );
};
